import { formatBusinessDuration } from "@/lib/businessDuration";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Loader2, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateRange } from "react-day-picker";
import Layout from "@/components/layout";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import { useAnalyticsStore } from "@/modules/analytics/analyticsStore";
import { StatCard } from "@/modules/analytics/components/StatCard";
import {
  OpportunityCountResponseSchema,
  OpportunityMetricsResponse,
  OpportunityMetricsResponseSchema,
  OpportunityValueOverTimePoint,
  OpportunityValueOverTimeResponseSchema,
  OpportunityTimingAnalyticsResponse,
  OpportunityTimingAnalyticsResponseSchema,
} from "@forgepoint/shared";
import { OpportunityTimingTable } from "@/modules/analytics/components/OpportunityTimingTable";

const RANGE_PRESETS = [
  { value: "last30", label: "Last 30 days" },
  { value: "last90", label: "Last 90 days" },
  { value: "ytd", label: "Year to date" },
  { value: "custom", label: "Custom range" },
] as const;

type RangePreset = (typeof RANGE_PRESETS)[number]["value"];

type DateParams = {
  startDate: string;
  endDate: string;
};

const formatCurrency = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) {
    return "--";
  }
  const amount = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(amount)) {
    return "--";
  }
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatPercent = (value: number | null) => {
  if (value === null) {
    return "--";
  }
  return `${(value * 100).toFixed(1)}%`;
};

const formatHours = (seconds: number | null) => {
  if (seconds === null) {
    return "--";
  }
  return `${(seconds / 3600).toFixed(1)}h`;
};

const getPresetRange = (preset: RangePreset, now = new Date()): DateRange => {
  const end = new Date(now);
  const start = new Date(now);

  if (preset === "last90") {
    start.setUTCDate(start.getUTCDate() - 89);
  } else if (preset === "ytd") {
    start.setUTCMonth(0, 1);
  } else if (preset === "custom") {
    return { from: start, to: end };
  } else {
    start.setUTCDate(start.getUTCDate() - 29);
  }

  start.setUTCHours(0, 0, 0, 0);
  end.setUTCHours(23, 59, 59, 999);
  return { from: start, to: end };
};

const toDateParams = (range?: DateRange): DateParams | null => {
  if (!range?.from || !range.to) {
    return null;
  }
  const startDate = new Date(range.from);
  const endDate = new Date(range.to);
  startDate.setUTCHours(0, 0, 0, 0);
  endDate.setUTCHours(23, 59, 59, 999);

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
};

const formatRangeLabel = (range?: DateRange) => {
  if (!range?.from || !range.to) {
    return "Select dates";
  }
  const startLabel = range.from.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const endLabel = range.to.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${startLabel} - ${endLabel}`;
};

export default function AnalyticsController() {
  const viewsSummary = useAnalyticsStore((s) => s.viewsSummary);
  const viewsSummaryLoading = useAnalyticsStore((s) => s.viewsSummaryLoading);
  const viewsSummaryError = useAnalyticsStore((s) => s.viewsSummaryError);
  const fetchViewsSummary = useAnalyticsStore((s) => s.fetchViewsSummary);

  const [rangePreset, setRangePreset] = useState<RangePreset>("last30");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() =>
    getPresetRange("last30"),
  );

  const [opportunityMetrics, setOpportunityMetrics] =
    useState<OpportunityMetricsResponse | null>(null);
  const [opportunityCount, setOpportunityCount] = useState<number | null>(null);
  const [acceptedCount, setAcceptedCount] = useState<number | null>(null);
  const [activeQuoteCount, setActiveQuoteCount] = useState<number | null>(null);
  const [sentQuoteCount, setSentQuoteCount] = useState<number | null>(null);
  const [valueOverTime, setValueOverTime] = useState<
    OpportunityValueOverTimePoint[]
  >([]);

  const [timingData, setTimingData] =
    useState<OpportunityTimingAnalyticsResponse | null>(null);
  const [timingLoading, setTimingLoading] = useState(false);
  const [timingError, setTimingError] = useState<string | null>(null);

  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [valueLoading, setValueLoading] = useState(false);
  const [valueError, setValueError] = useState<string | null>(null);

  const dateParams = useMemo(() => toDateParams(dateRange), [dateRange]);

  useEffect(() => {
    fetchViewsSummary();
  }, [fetchViewsSummary]);

  const loadSummary = useCallback(
    async (params: DateParams) => {
      setSummaryLoading(true);
      setSummaryError(null);

      try {
        const [
          metricsResponse,
          countResponse,
          acceptedResponse,
          activeQuoteResponse,
          sentQuoteResponse,
        ] = await Promise.all([
          fetchApi("analytics/opportunities/metrics", "GET", {
            params,
          }),
          fetchApi("analytics/opportunities/count", "GET", {
            params,
          }),
          fetchApi("analytics/opportunities/count", "GET", {
            params: { ...params, statuses: "ACCEPTED" },
          }),
          fetchApi("analytics/opportunities/count", "GET", {
            params: {
              ...params,
              statuses: "QUOTE_IN_PROGRESS,SENT,ACCEPTED,LOST",
            },
          }),
          fetchApi("analytics/opportunities/count", "GET", {
            params: { ...params, statuses: "SENT,ACCEPTED,LOST" },
          }),
        ]);

        if (!metricsResponse.ok) {
          throw new Error("Failed to load opportunity metrics");
        }
        if (!countResponse.ok || !acceptedResponse.ok) {
          throw new Error("Failed to load opportunity counts");
        }
        if (!activeQuoteResponse.ok || !sentQuoteResponse.ok) {
          throw new Error("Failed to load opportunity funnel counts");
        }

        const metrics = OpportunityMetricsResponseSchema.parse(
          metricsResponse.data,
        );
        const countData = OpportunityCountResponseSchema.parse(
          countResponse.data,
        );
        const acceptedData = OpportunityCountResponseSchema.parse(
          acceptedResponse.data,
        );
        const activeQuoteData = OpportunityCountResponseSchema.parse(
          activeQuoteResponse.data,
        );
        const sentQuoteData = OpportunityCountResponseSchema.parse(
          sentQuoteResponse.data,
        );

        setOpportunityMetrics(metrics);
        setOpportunityCount(countData.count);
        setAcceptedCount(acceptedData.count);
        setActiveQuoteCount(activeQuoteData.count);
        setSentQuoteCount(sentQuoteData.count);
      } catch (error) {
        captureException(error as Error);
        setSummaryError("Unable to load sales summary");
      } finally {
        setSummaryLoading(false);
      }
    },
    [
      setOpportunityMetrics,
      setOpportunityCount,
      setAcceptedCount,
      setActiveQuoteCount,
      setSentQuoteCount,
    ],
  );

  const loadValueOverTime = useCallback(async (params: DateParams) => {
    setValueLoading(true);
    setValueError(null);

    try {
      const response = await fetchApi(
        "analytics/opportunities/value-over-time",
        "GET",
        { params },
      );
      if (!response.ok) {
        throw new Error("Failed to load opportunity value over time");
      }
      const data = OpportunityValueOverTimeResponseSchema.parse(response.data);
      setValueOverTime(data.points);
    } catch (error) {
      captureException(error as Error);
      setValueError("Unable to load opportunity value trend");
    } finally {
      setValueLoading(false);
    }
  }, []);

  const loadTiming = useCallback(async (params: DateParams) => {
    setTimingLoading(true);
    setTimingError(null);
    setTimingData(null);

    try {
      const response = await fetchApi("analytics/opportunities/timing", "GET", {
        params,
      });
      if (!response.ok) {
        throw new Error("Failed to load opportunity timing");
      }
      const data = OpportunityTimingAnalyticsResponseSchema.parse(
        response.data,
      );
      setTimingData(data);
    } catch (error) {
      captureException(error as Error);
      setTimingError("Unable to load opportunity timing");
    } finally {
      setTimingLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!dateParams) {
      return;
    }
    loadSummary(dateParams);
    loadValueOverTime(dateParams);
    loadTiming(dateParams);
  }, [dateParams, loadSummary, loadValueOverTime, loadTiming]);

  const valueChartData = useMemo(
    () =>
      valueOverTime.map((point) => ({
        weekStart: point.weekStart,
        value: Number(point.value),
        weightedValue: Number(point.weightedValue),
      })),
    [valueOverTime],
  );

  const baseCount = opportunityCount ?? 0;
  const poCount = acceptedCount ?? 0;
  const activeCount = activeQuoteCount ?? 0;
  const sentCount = sentQuoteCount ?? 0;
  const funnelSteps = [
    {
      label: "New Opportunities",
      value: baseCount,
      percent: 1,
    },
    {
      label: "Quote In Progress or Later",
      value: activeCount,
      percent: baseCount > 0 ? activeCount / baseCount : 1,
    },
    {
      label: "Quote Completed or Later",
      value: sentCount,
      percent: baseCount > 0 ? sentCount / baseCount : 1,
    },
    {
      label: "Won",
      value: poCount,
      percent: baseCount > 0 ? poCount / baseCount : 1,
    },
  ];

  return (
    <Layout breadcrumbs={[{ label: "Analytics", current: true }]}>
      <div className="space-y-4 p-3 sm:space-y-6 sm:p-4 overflow-x-hidden">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold sm:text-2xl">
              Sales Analytics
            </h1>
            <p className="text-sm text-muted-foreground sm:text-base">
              Monitor pipeline momentum, conversion, and quote velocity.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={rangePreset}
              onValueChange={(value) => {
                const preset =
                  RANGE_PRESETS.find((option) => option.value === value)
                    ?.value ?? "custom";
                setRangePreset(preset);
                if (preset !== "custom") {
                  setDateRange(getPresetRange(preset));
                }
              }}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                {RANGE_PRESETS.map((preset) => (
                  <SelectItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  <span className="text-sm">{formatRangeLabel(dateRange)}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => {
                    setDateRange(range);
                    setRangePreset("custom");
                  }}
                  numberOfMonths={2}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {summaryError && (
          <Card className="p-4 text-destructive">{summaryError}</Card>
        )}

        <div className="grid gap-3 sm:gap-4 lg:grid-cols-4">
          <StatCard
            label="Won (Value)"
            value={formatCurrency(opportunityMetrics?.wonDealSize ?? null)}
            loading={summaryLoading}
          />
          <StatCard
            label="Weighted Pipeline"
            value={formatCurrency(
              opportunityMetrics?.weightedTotalDealSize ?? null,
            )}
            loading={summaryLoading}
            tooltip="Deal values adjusted by each opportunity's probability of closing"
          />
          <StatCard
            label="Win Rate"
            value={formatPercent(opportunityMetrics?.winRate ?? null)}
            loading={summaryLoading}
          />
          <StatCard
            label="Avg Time to Quote (Business)"
            value={formatBusinessDuration(
              opportunityMetrics?.avgTimeToQuoteBusinessSeconds ?? null,
            )}
            loading={summaryLoading}
          />
        </div>

        <div className="grid gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-5">
          <StatCard
            label="New Opportunities"
            value={opportunityCount ?? "--"}
            loading={summaryLoading}
          />
          <StatCard
            label="Quotes Completed"
            value={sentQuoteCount ?? "--"}
            loading={summaryLoading}
          />
          <StatCard
            label="Won (Count)"
            value={acceptedCount ?? "--"}
            loading={summaryLoading}
          />
          <StatCard
            label="Avg Deal Size"
            value={formatCurrency(opportunityMetrics?.avgDealSize ?? null)}
            loading={summaryLoading}
          />
          <StatCard
            label="Avg Time Quote → Won (Business)"
            value={formatBusinessDuration(
              opportunityMetrics?.avgQuoteToCloseBusinessSeconds ?? null,
            )}
            loading={summaryLoading}
          />
        </div>

        <div className="grid gap-3 sm:gap-4 lg:grid-cols-2">
          <Card className="p-3 sm:p-4 overflow-hidden min-w-0">
            <div>
              <h2 className="text-sm font-medium sm:text-base">
                Opportunity Value Over Time
              </h2>
              <p className="text-xs text-muted-foreground sm:text-sm">
                Weekly totals — raw value vs. probability-weighted value.
              </p>
            </div>
            <Separator className="my-2 sm:my-3" />
            {valueLoading ? (
              <LoadingState />
            ) : valueError ? (
              <p className="text-sm text-destructive">{valueError}</p>
            ) : (
              <ChartContainer
                config={{
                  value: { label: "Value", color: "hsl(216 95% 60%)" },
                  weightedValue: {
                    label: "Weighted",
                    color: "hsl(262 83% 58%)",
                  },
                }}
                className="h-[260px] sm:h-[320px] w-full"
              >
                <BarChart data={valueChartData} margin={{ left: -8, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="weekStart" tick={{ fontSize: 10 }} />
                  <YAxis
                    tickFormatter={(value) =>
                      formatCurrency(Number(value) || 0)
                    }
                    width={50}
                    tick={{ fontSize: 10 }}
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        formatter={(value) =>
                          formatCurrency(Number(value) || 0)
                        }
                      />
                    }
                  />
                  <Bar
                    dataKey="value"
                    fill="var(--color-value)"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="weightedValue"
                    fill="var(--color-weightedValue)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ChartContainer>
            )}
          </Card>

          <Card className="p-3 sm:p-4 overflow-hidden">
            <div>
              <h2 className="text-sm font-medium sm:text-base">
                Revenue Conversion Funnel
              </h2>
              <p className="text-xs text-muted-foreground sm:text-sm">
                Conversion from new opportunities to won deals.
              </p>
            </div>
            <Separator className="my-2 sm:my-3" />
            <div className="space-y-3">
              {funnelSteps.map((step) => {
                const percentLabel = `${Math.round(step.percent * 100)}%`;
                const widthPercent = `${Math.max(step.percent, 0.12) * 100}%`;

                return (
                  <div key={step.label} className="space-y-2">
                    <div className="flex justify-center">
                      <div
                        className="rounded-lg bg-[hsl(214_38%_34%)] px-4 py-3 text-center text-sm font-medium text-white"
                        style={{ width: widthPercent }}
                      >
                        <div className="text-xs text-white/80">
                          {step.label}
                        </div>
                        <div className="text-base font-semibold mt-1">
                          {summaryLoading ? (
                            <Loader2 className="mx-auto h-4 w-4 animate-spin text-white" />
                          ) : (
                            step.value
                          )}
                        </div>
                        <div className="text-xs text-white/80 mt-1">
                          {summaryLoading ? "--" : percentLabel}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        <Card className="p-3 sm:p-4 overflow-hidden min-w-0">
          <div className="flex flex-col gap-1">
            <h2 className="text-sm font-medium sm:text-base">
              Viewer engagement
            </h2>
            <p className="text-xs text-muted-foreground sm:text-sm">
              Weekly view totals and top buyer locations.
            </p>
          </div>
          <Separator className="my-2 sm:my-3" />
          {viewsSummaryLoading && !viewsSummary ? (
            <LoadingState />
          ) : viewsSummaryError ? (
            <p className="text-sm text-destructive">{viewsSummaryError}</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <ChartContainer
                config={{
                  views: { label: "Views", color: "hsl(221 83% 53%)" },
                }}
                className="h-[200px] sm:h-[260px] w-full min-w-0"
              >
                <BarChart
                  data={viewsSummary?.viewsPerWeek ?? []}
                  margin={{ left: -8, right: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="weekStart" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} width={30} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar
                    dataKey="count"
                    fill="var(--color-views)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ChartContainer>
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground">
                  Top cities (30 days)
                </h3>
                <div className="space-y-2">
                  {(viewsSummary?.topCities ?? []).map((city) => (
                    <div
                      key={city.city}
                      className="flex items-center justify-between rounded-lg border p-3"
                    >
                      <span className="font-medium">{city.city}</span>
                      <span className="text-sm text-muted-foreground">
                        {city.count} views
                      </span>
                    </div>
                  ))}
                  {viewsSummary && viewsSummary.topCities.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      No view data yet.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>

        {timingError && (
          <Card className="p-4 text-destructive">{timingError}</Card>
        )}

        <div className="grid gap-3 sm:gap-4 lg:grid-cols-3">
          <StatCard
            label="Avg Agent Working Time"
            value={formatHours(
              timingData?.aggregates.avgAgentWorkingSeconds ?? null,
            )}
            loading={timingLoading}
            tooltip="Average time the agent spent actively working across opportunities"
          />
          <StatCard
            label="Avg Approval Wait Time"
            value={formatHours(
              timingData?.aggregates.avgWaitingApprovalSeconds ?? null,
            )}
            loading={timingLoading}
            tooltip="Average time opportunities spent waiting for human approval"
          />
          <StatCard
            label="Agent Waiting Percentage"
            value={
              timingData?.aggregates.agentWaitingPercent !== null &&
              timingData?.aggregates.agentWaitingPercent !== undefined
                ? `${timingData.aggregates.agentWaitingPercent.toFixed(1)}%`
                : "--"
            }
            loading={timingLoading}
            tooltip="Percentage of total time spent waiting for approval vs. agent work"
          />
        </div>

        <Card className="p-3 sm:p-4 overflow-hidden min-w-0">
          <div>
            <h2 className="text-sm font-medium sm:text-base">
              Opportunity Timing Details
            </h2>
            <p className="text-xs text-muted-foreground sm:text-sm">
              Per-opportunity breakdown with timing bars (newest first).
            </p>
          </div>
          <Separator className="my-2 sm:my-3" />
          {timingLoading ? (
            <LoadingState />
          ) : timingError ? (
            <p className="text-sm text-destructive">{timingError}</p>
          ) : (
            <OpportunityTimingTable rows={timingData?.rows ?? []} />
          )}
        </Card>
      </div>
    </Layout>
  );
}

const LoadingState = () => (
  <div className="flex h-[180px] items-center justify-center text-muted-foreground sm:h-[240px]">
    <Loader2 className="h-5 w-5 animate-spin" />
    <span className="ml-2 text-sm">Loading</span>
  </div>
);
